import { supabase } from './supabase'
import { messageErreurBoitier } from './gps'
import type { Boitier, VehiculeOps } from './types'

/**
 * Boîtiers GPS (migration 0026) — lectures et écritures.
 *
 * ⚠️ La console se redéploie seule à chaque push, les migrations s'appliquent à
 * la main : tant que la 0026 n'est pas poussée, `gps_devices` n'existe pas. Les
 * lectures ici sont donc TOLÉRANTES (liste vide + `ok: false`) — elles ne doivent
 * jamais faire échouer le chargement de toute la console.
 */

const COLONNES = 'id,unique_id,label,vehicle_id,driver_id,active,last_lat,last_lng,last_fix_at,last_received_at,last_speed_kmh,last_course'

const nbOuNull = (v: unknown): number | null => {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}
const texteOuNull = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null)

export function versBoitier(r: Record<string, unknown>): Boitier {
  return {
    id: String(r.id ?? ''),
    unique_id: String(r.unique_id ?? ''),
    label: texteOuNull(r.label),
    vehicle_id: texteOuNull(r.vehicle_id),
    driver_id: texteOuNull(r.driver_id),
    active: r.active !== false,
    last_lat: nbOuNull(r.last_lat),
    last_lng: nbOuNull(r.last_lng),
    last_fix_at: texteOuNull(r.last_fix_at),
    last_received_at: texteOuNull(r.last_received_at),
    last_speed_kmh: nbOuNull(r.last_speed_kmh),
    last_course: nbOuNull(r.last_course),
  }
}

export async function lireBoitiers(orgId: string): Promise<{ boitiers: Boitier[]; ok: boolean }> {
  const { data, error } = await supabase.from('gps_devices').select(COLONNES).eq('org_id', orgId).order('created_at')
  if (error) return { boitiers: [], ok: false }
  return { boitiers: ((data ?? []) as Record<string, unknown>[]).map(versBoitier), ok: true }
}

export async function lireVehicules(orgId: string): Promise<VehiculeOps[]> {
  const { data, error } = await supabase.from('vehicles').select('id,plate,model,type').eq('org_id', orgId).order('plate')
  if (error) return []
  return ((data ?? []) as Record<string, unknown>[]).map((v) => ({
    id: String(v.id),
    plate: typeof v.plate === 'string' ? v.plate : '',
    model: typeof v.model === 'string' ? v.model : '',
    type: typeof v.type === 'string' ? v.type : '',
  }))
}

/** Source de la position de chaque livreur (`drivers.position_source`, 0026) ; `null` si la colonne n'existe pas encore. */
export async function lireSourcesPosition(orgId: string): Promise<Map<string, 'app' | 'boitier'> | null> {
  const { data, error } = await supabase.from('drivers').select('id,position_source').eq('org_id', orgId)
  if (error) return null
  return new Map(
    ((data ?? []) as { id: string; position_source: string | null }[]).map((d) => [d.id, d.position_source === 'boitier' ? 'boitier' : 'app']),
  )
}

export interface SaisieBoitier {
  unique_id: string
  label: string
  vehicle_id: string | null
  driver_id: string | null
}

export async function ajouterBoitier(orgId: string, s: SaisieBoitier): Promise<void> {
  const { error } = await supabase.from('gps_devices').insert({
    org_id: orgId,
    unique_id: s.unique_id.trim(),
    label: s.label.trim() || null,
    vehicle_id: s.vehicle_id,
    driver_id: s.driver_id,
  })
  if (error) throw new Error(messageErreurBoitier(error))
}

export async function modifierBoitier(
  id: string,
  patch: Partial<{ label: string | null; vehicle_id: string | null; driver_id: string | null; active: boolean }>,
): Promise<void> {
  const { error } = await supabase.from('gps_devices').update(patch).eq('id', id)
  if (error) throw new Error(messageErreurBoitier(error))
}

export async function supprimerBoitier(id: string): Promise<void> {
  const { error } = await supabase.from('gps_devices').delete().eq('id', id)
  if (error) throw new Error(messageErreurBoitier(error))
}

/**
 * Génère (ou régénère) le secret de l'organisation. Il n'est renvoyé QU'UNE FOIS :
 * la base n'en garde que le hash, aucune lecture ne peut le redonner. Régénérer
 * invalide l'ancien — Traccar cesse alors d'être accepté jusqu'à ce qu'on y colle
 * le nouveau.
 */
export async function regenererSecretGps(orgId: string): Promise<string> {
  const { data, error } = await supabase.rpc('regenerer_secret_gps', { p_org: orgId })
  if (error) throw new Error(messageErreurBoitier(error))
  return String(data)
}

export async function etatSourceGps(orgId: string): Promise<{ configuree: boolean; miseAJour: string | null } | null> {
  const { data, error } = await supabase.rpc('etat_source_gps', { p_org: orgId })
  if (error) return null
  const ligne = (Array.isArray(data) ? data[0] : data) as { configuree?: boolean; mise_a_jour?: string | null } | undefined
  return { configuree: !!ligne?.configuree, miseAJour: ligne?.mise_a_jour ?? null }
}

/** Positions des boîtiers en direct (`gps_devices` est publiée dans `supabase_realtime`, 0026). */
export function suivreBoitiers(orgId: string, surBoitier: (b: Boitier) => void, surSuppression: (id: string) => void): () => void {
  const canal = supabase
    .channel('ops-boitiers')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'gps_devices', filter: `org_id=eq.${orgId}` }, (charge) => {
      if (charge.eventType === 'DELETE') {
        const id = (charge.old as { id?: string } | null)?.id
        if (id) surSuppression(id)
        return
      }
      const n = charge.new as Record<string, unknown> | null
      if (n && n.id) surBoitier(versBoitier(n))
    })
    .subscribe()
  return () => {
    void supabase.removeChannel(canal)
  }
}
